import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import currentTimeZone from '../../../../../config/timeZone';
import DefaultApiParameters from '../../../../../enum/DefaultApiParameters';
import {
  OcppLogsProperties,
  OcppLogsRequest,
} from '../../../../../interface/device-ocpp-logs.interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  fetchOcppLogs,
  fetchOcppLogsFilters,
  updateOcppLogsFilters,
  refreshOcppLogs,
} from '../../../../../redux/slices/DeviceOcppLogsSilce';
import { RootState } from '../../../../../redux/store';
import CustomDateRangePicker from '../../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import OcppLogSidePanel from '../../../../../shared-components/table-side-panel/OcppLogsSidePanel';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import getInitialDateRange from '../../../../../shared-functions/GetInitialDateRange';
import { capitalizeFirstLetter } from '../../../../../shared-functions/StringFunctions';

/**
 * Component to disable charging schedule based on charging point
 * @returns {any} The result
 */
function OcppLogs() {
  const { t } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const { id: chargerId } = useParams();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const lastFetchReference = useRef<string>('');
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() =>
    getInitialDateRange(),
  );

  const { ocppLogs, limit, page, loading, totalPages, totalResults, filterListLoading, filters } =
    useAppSelector((state: RootState) => state.ocppLogs);
  const { selectedChargingPoint } = useAppSelector((s) => s.device);

  /**
   * Get query parameters for API call
   * @param {any} activeFilters - The active filters
   * @returns {any} The query parameters
   */
  const getQueryParameters = (activeFilters: any[]) => {
    const queryParameters = GetParametersFromCommonFilters(activeFilters);
    if (dateRange.startDate) {
      queryParameters.startDate = dateRange.startDate;
      queryParameters.timeZone = currentTimeZone;
    }
    if (dateRange.endDate) {
      queryParameters.endDate = dateRange.endDate;
      if (!queryParameters.timeZone) queryParameters.timeZone = currentTimeZone;
    }
    return queryParameters;
  };

  const column = [
    {
      label: t('uniqueId'),
      accessorKey: 'uniqueId' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 45,
    },
    {
      label: t('date'),
      accessorKey: 'date' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 40,
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 40,
    },
    {
      label: t('type'),
      accessorKey: 'type' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 20,
      /**
       * Display type in ocpp table
       * @param {any} row - The row
       * @returns {any} The result
       */
      customData: (row: OcppLogsProperties) => {
        const typeLower = row?.type?.toLowerCase() || '';
        let typeClass = typeLower;
        if (typeLower === 'in') {
          typeClass = 'in';
        } else if (typeLower === 'out') {
          typeClass = 'out';
        }
        return (
          <div className={`ocpp-logs-type ${typeClass}`}>
            <p className={typeClass}>{capitalizeFirstLetter(row?.type)}</p>
          </div>
        );
      },
    },
    {
      label: t('action'),
      accessorKey: 'action' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 45,
    },
    {
      label: t('heartbeatCount'),
      accessorKey: 'heartbeatcount' as keyof OcppLogsProperties,
      sortAllow: false,
      columnWidth: 45,
    },
    {
      label: t('message'),
      accessorKey: 'message' as keyof OcppLogsProperties,
      sortAllow: false,
    },
  ];

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: OcppLogsRequest) => {
    const queryParameters = getQueryParameters(filters);
    const chargingPointSn =
      selectedChargingPoint?.chargingPointSn || (chargerId?.startsWith('SN') ? chargerId : '');

    if (!chargingPointSn) return;

    const requestPayload = {
      ...value,
      ...queryParameters,
      chargingPointSn,
    };
    const fetchKey = JSON.stringify(requestPayload);

    if (lastFetchReference.current === fetchKey) return;
    lastFetchReference.current = fetchKey;

    dispatch(fetchOcppLogs(requestPayload));
  };

  /**
   * Function to add search params, side panel will open automatically
   * @param {any} logId - The logId
   */
  const handleOpenSidePanelOnRowClick = (logId: string) => {
    // Set or update the logId param
    searchParameters.set('logId', logId);

    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
  };

  /**
   * Function to refresh the table
   */
  const handleRefresh = () => {
    const queryParameters = getQueryParameters(filters);
    const chargingPointSn =
      selectedChargingPoint?.chargingPointSn || (chargerId?.startsWith('SN') ? chargerId : '');
    if (!chargingPointSn) return;

    dispatch(
      fetchOcppLogs({
        page,
        limit,
        chargingPointSn,
        ...queryParameters,
      }),
    );
  };

  /**
   * Function to auto refresh the table
   */
  const handleAutoRefresh = () => {
    const queryParameters = getQueryParameters(filters);
    const chargingPointSn =
      selectedChargingPoint?.chargingPointSn || (chargerId?.startsWith('SN') ? chargerId : '');

    if (!chargingPointSn) return;

    dispatch(
      refreshOcppLogs({
        page,
        limit,
        chargingPointSn,
        ...queryParameters,
      }),
    );
  };

  useEffect(() => {
    const queryParameters = getQueryParameters(filters);
    const chargingPointSn =
      selectedChargingPoint?.chargingPointSn || (chargerId?.startsWith('SN') ? chargerId : '');

    if (!chargingPointSn) return;

    const requestPayload = {
      page: DefaultApiParameters.page,
      limit: DefaultApiParameters.limit,
      chargingPointSn,
      ...queryParameters,
    };
    const fetchKey = JSON.stringify(requestPayload);

    if (lastFetchReference.current === fetchKey) return;
    lastFetchReference.current = fetchKey;

    dispatch(fetchOcppLogs(requestPayload));
  }, [filters, selectedChargingPoint, chargerId, dateRange.startDate, dateRange.endDate]);

  return (
    <div className="device-charging-schedules-wrap">
      <div className="device-charging-schedules-table">
        <CustomTable
          header={column}
          data={ocppLogs}
          serialNo
          filter={{}}
          filterClearFunction={() => {}}
          filterFunction={() => {}}
          actionButton={false}
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          pageLimitChange={(value: any) => pageNateLimitChange(value)}
          sizeChangeButtonRequired
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="logId"
          rowClickSidePanel={<OcppLogSidePanel fetchIdFrom="queryParams" />}
          filters={filters}
          filtersToDispatch={updateOcppLogsFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchOcppLogsFilters}
          version="v2"
          height={400}
          refreshRequired
          handleRefresh={handleRefresh}
          hoverLabel="Refresh"
          refershCurrentPages={handleRefresh}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          rightTopContent={
            <div className="date-content">
              <CustomDateRangePicker
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
                placeholder="DD-MM-YYYY  - DD-MM-YYYY"
                defaultPreset="last14Days"
                disableDates="future"
                requiredTime
              />
            </div>
          }
        />
      </div>
    </div>
  );
}

export default OcppLogs;
